import { useState, useEffect, useRef } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle
} from "../components/ui/dialog";
import {
  Package, Plus, Edit, Trash2, Search, Camera, X, Tag, AlertTriangle, ImageIcon
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const etatConfig = {
  neuf: { label: "Neuf", color: "bg-blue-100 text-blue-800" },
  occasion: { label: "Occasion", color: "bg-amber-100 text-amber-800" },
  reconditionne: { label: "Reconditionné", color: "bg-green-100 text-green-800" },
  comme_neuf: { label: "Comme neuf", color: "bg-teal-100 text-teal-800" },
};

const categories = [
  "chargeur", "cable", "ecouteurs", "coque", "vitre_protection",
  "souris", "clavier", "cartouche", "cle_usb", "carte_memoire",
  "batterie", "adaptateur", "ecran", "disque_dur", "memoire_ram",
  "ordinateur", "smartphone", "tablette", "imprimante", "autre"
];

const categoryLabels = {
  chargeur: "Chargeur", cable: "Câble", ecouteurs: "Écouteurs", coque: "Coque",
  vitre_protection: "Vitre de protection", souris: "Souris", clavier: "Clavier",
  cartouche: "Cartouche", cle_usb: "Clé USB", carte_memoire: "Carte mémoire",
  batterie: "Batterie", adaptateur: "Adaptateur", ecran: "Écran",
  disque_dur: "Disque dur", memoire_ram: "Mémoire RAM", ordinateur: "Ordinateur",
  smartphone: "Smartphone", tablette: "Tablette", imprimante: "Imprimante", autre: "Autre"
};

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("neuf");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const fileInputRef = useRef(null);
  const dialogFileRef = useRef(null);
  const [formData, setFormData] = useState({
    reference: "", nom: "", categorie: "autre", marque: "",
    etat: "neuf", cout_achat: 0, prix_vente: 0, quantite_stock: 0,
    seuil_alerte: 5, description: ""
  });

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await API.get("/products", { params: { limit: 200 } });
      setProducts(res.data.products);
      setTotal(res.data.total);
    } catch (error) {
      toast.error("Erreur chargement produits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const isOccasion = activeTab !== "neuf";

  const openCreate = () => {
    setEditingProduct(null);
    setPendingPhotos([]);
    setFormData({
      reference: "", nom: "", categorie: "autre", marque: "",
      etat: isOccasion ? "occasion" : "neuf",
      cout_achat: 0, prix_vente: 0, quantite_stock: 0,
      seuil_alerte: 5, description: ""
    });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditingProduct(p);
    setPendingPhotos([]);
    setFormData({
      reference: p.reference, nom: p.nom, categorie: p.categorie,
      marque: p.marque || "", etat: p.etat || "neuf",
      cout_achat: p.cout_achat, prix_vente: p.prix_vente,
      quantite_stock: p.quantite_stock, seuil_alerte: p.seuil_alerte,
      description: p.description || ""
    });
    setDialogOpen(true);
  };

  const addPendingPhotos = (files) => {
    const remaining = 5 - pendingPhotos.length - (editingProduct?.photos?.length || 0);
    const toAdd = Array.from(files).slice(0, remaining).map(f => ({
      file: f,
      preview: URL.createObjectURL(f)
    }));
    setPendingPhotos(prev => [...prev, ...toAdd]);
  };

  const removePendingPhoto = (index) => {
    setPendingPhotos(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let product;
      const isEdit = !!editingProduct;
      if (isEdit) {
        const res = await API.put(`/products/${editingProduct.id}`, formData);
        product = res.data;
      } else {
        const res = await API.post("/products", formData);
        product = res.data;
      }
      
      // Upload pending photos
      const productId = product?.id || editingProduct?.id;
      if (pendingPhotos.length > 0 && productId) {
        const fd = new FormData();
        for (const p of pendingPhotos) fd.append("files", p.file);
        await API.post(`/products/${productId}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      
      setPendingPhotos([]);
      setDialogOpen(false);
      // Delay fetch to let Dialog unmount cleanly (avoids removeChild error with Radix portals)
      setTimeout(() => {
        toast.success(isEdit ? "Produit modifié" : "Produit créé");
        fetchProducts();
      }, 200);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Supprimer ce produit ?")) return;
    try {
      await API.delete(`/products/${id}`);
      toast.success("Produit supprimé");
      fetchProducts();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handlePhotoUpload = async (productId, files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      await API.post(`/products/${productId}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Photo(s) ajoutée(s)");
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoDelete = async (productId, photoUrl) => {
    try {
      await API.delete(`/products/${productId}/photos`, { params: { photo_url: photoUrl } });
      toast.success("Photo supprimée");
      fetchProducts();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  // Filter products
  const neufProducts = products.filter(p => !p.etat || p.etat === "neuf");
  const occasionProducts = products.filter(p => ["occasion", "reconditionne", "comme_neuf"].includes(p.etat));

  const filteredNeuf = search
    ? neufProducts.filter(p => p.nom?.toLowerCase().includes(search.toLowerCase()) || p.reference?.toLowerCase().includes(search.toLowerCase()))
    : neufProducts;

  const filteredOccasion = search
    ? occasionProducts.filter(p => p.nom?.toLowerCase().includes(search.toLowerCase()) || p.reference?.toLowerCase().includes(search.toLowerCase()))
    : occasionProducts;

  const lowStockNeuf = neufProducts.filter(p => p.quantite_stock <= p.seuil_alerte).length;

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Produits</h1>
          <p className="text-muted-foreground">{total} produits au catalogue</p>
        </div>
        <Button onClick={openCreate} data-testid="add-product-btn">
          <Plus className="h-4 w-4 mr-2" />
          {isOccasion ? "Nouveau produit d'occasion" : "Nouveau produit"}
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher un produit..."
          className="pl-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="product-search"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="neuf">
            <Package className="h-4 w-4 mr-1.5" />
            Produits boutique ({neufProducts.length})
          </TabsTrigger>
          <TabsTrigger value="occasion">
            <Tag className="h-4 w-4 mr-1.5" />
            Bonnes affaires / Occasion ({occasionProducts.length})
          </TabsTrigger>
        </TabsList>

        {/* ===== TAB NEUF - Liste simple ===== */}
        <TabsContent value="neuf">
          {lowStockNeuf > 0 && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm mb-4">
              <AlertTriangle className="h-4 w-4" />
              <span>{lowStockNeuf} produit(s) en stock bas</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : filteredNeuf.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <Package className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="font-medium">Aucun produit boutique</p>
                <Button variant="link" onClick={openCreate}>Ajouter un produit</Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-14">Photo</TableHead>
                        <TableHead>Référence</TableHead>
                        <TableHead>Nom</TableHead>
                        <TableHead>Marque</TableHead>
                        <TableHead>Catégorie</TableHead>
                        <TableHead className="text-right">Prix vente</TableHead>
                        <TableHead className="text-right">Stock</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredNeuf.map((p) => (
                        <TableRow key={p.id} data-testid={`neuf-row-${p.id}`}>
                          <TableCell>
                            {p.photos?.length > 0 ? (
                              <img src={`${BACKEND}${p.photos[0]}`} alt="" className="w-10 h-10 rounded object-cover" />
                            ) : (
                              <div className="w-10 h-10 rounded bg-secondary/50 flex items-center justify-center">
                                <ImageIcon className="h-4 w-4 text-muted-foreground/40" />
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-sm">{p.reference}</TableCell>
                          <TableCell className="font-medium">{p.nom}</TableCell>
                          <TableCell>{p.marque || "-"}</TableCell>
                          <TableCell>{categoryLabels[p.categorie] || p.categorie}</TableCell>
                          <TableCell className="text-right font-mono">{p.prix_vente?.toFixed(2)}€</TableCell>
                          <TableCell className="text-right">
                            <span className={p.quantite_stock <= p.seuil_alerte ? "text-red-600 font-bold" : ""}>
                              {p.quantite_stock}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => openEdit(p)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(p.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ===== TAB OCCASION - Cartes avec photos ===== */}
        <TabsContent value="occasion">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : filteredOccasion.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <Tag className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="font-medium">Aucun produit d'occasion</p>
                <p className="text-sm text-muted-foreground mb-3">
                  Ajoutez vos produits d'occasion, reconditionnés ou comme neufs ici
                </p>
                <Button onClick={openCreate}>
                  <Plus className="h-4 w-4 mr-2" /> Ajouter un produit d'occasion
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredOccasion.map((product) => {
                const etat = etatConfig[product.etat] || etatConfig.occasion;
                const photos = product.photos || [];
                return (
                  <Card key={product.id} className="overflow-hidden" data-testid={`occasion-card-${product.id}`}>
                    {/* Photo area */}
                    <div className="relative h-44 bg-secondary/30 flex items-center justify-center overflow-hidden">
                      {photos.length > 0 ? (
                        <img src={`${BACKEND}${photos[0]}`} alt={product.nom} className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex flex-col items-center text-muted-foreground/40">
                          <ImageIcon className="h-12 w-12 mb-1" />
                          <span className="text-xs">Aucune photo</span>
                        </div>
                      )}
                      {photos.length > 1 && (
                        <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                          +{photos.length - 1} photo{photos.length > 2 ? "s" : ""}
                        </span>
                      )}
                      <Badge className={`absolute top-2 left-2 ${etat.color}`}>{etat.label}</Badge>
                    </div>

                    <CardContent className="p-4 space-y-2">
                      <div>
                        <p className="font-semibold truncate">{product.nom}</p>
                        <p className="text-xs text-muted-foreground">{product.marque} - {product.reference}</p>
                      </div>
                      {product.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2">{product.description}</p>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold">{product.prix_vente?.toFixed(2)}€</span>
                        <span className="text-sm text-muted-foreground">Stock: {product.quantite_stock}</span>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-1 pt-1">
                        <Button
                          size="sm" variant="outline" className="flex-1"
                          onClick={() => {
                            fileInputRef.current?.setAttribute("data-product-id", product.id);
                            fileInputRef.current?.click();
                          }}
                          disabled={photos.length >= 5 || uploading}
                          data-testid={`upload-photo-${product.id}`}
                        >
                          <Camera className="h-3.5 w-3.5 mr-1" /> Photo {photos.length}/5
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openEdit(product)}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleDelete(product.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>

                      {/* Photo thumbnails */}
                      {photos.length > 0 && (
                        <div className="flex gap-1.5 pt-1">
                          {photos.map((url, idx) => (
                            <div key={idx} className="relative group w-14 h-14 rounded-lg border overflow-hidden">
                              <img src={`${BACKEND}${url}`} alt="" className="w-full h-full object-cover" />
                              <button
                                className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                onClick={() => handlePhotoDelete(product.id, url)}
                              >
                                <X className="h-3.5 w-3.5 text-white" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Hidden file input for photo upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          const productId = e.target.getAttribute("data-product-id");
          if (productId && e.target.files.length) {
            handlePhotoUpload(productId, Array.from(e.target.files).slice(0, 5));
          }
          e.target.value = "";
        }}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>
                {editingProduct ? "Modifier le produit" : isOccasion ? "Nouveau produit d'occasion" : "Nouveau produit"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Référence *</Label>
                  <Input value={formData.reference} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} required data-testid="product-ref" />
                </div>
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} required data-testid="product-name" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <select
                    value={formData.categorie}
                    onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                    className="flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    {categories.map(c => (
                      <option key={c} value={c}>{categoryLabels[c] || c}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>État</Label>
                  <select
                    value={formData.etat}
                    onChange={(e) => setFormData({ ...formData, etat: e.target.value })}
                    className="flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    data-testid="product-etat"
                  >
                    <option value="neuf">Neuf</option>
                    <option value="occasion">Occasion</option>
                    <option value="reconditionne">Reconditionné</option>
                    <option value="comme_neuf">Comme neuf</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Marque</Label>
                <Input value={formData.marque} onChange={(e) => setFormData({ ...formData, marque: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prix d'achat (€)</Label>
                  <Input type="number" step="0.01" min="0" value={formData.cout_achat || ""} onChange={(e) => setFormData({ ...formData, cout_achat: parseFloat(e.target.value) || 0 })} data-testid="product-cost" />
                </div>
                <div className="space-y-2">
                  <Label>Prix de vente (€) *</Label>
                  <Input type="number" step="0.01" min="0" value={formData.prix_vente || ""} onChange={(e) => setFormData({ ...formData, prix_vente: parseFloat(e.target.value) || 0 })} required data-testid="product-price" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantité en stock</Label>
                  <Input type="number" min="0" value={formData.quantite_stock} onChange={(e) => setFormData({ ...formData, quantite_stock: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="space-y-2">
                  <Label>Seuil d'alerte</Label>
                  <Input type="number" min="0" value={formData.seuil_alerte} onChange={(e) => setFormData({ ...formData, seuil_alerte: parseInt(e.target.value) || 0 })} />
                </div>
              </div>
              {/* Description */}
              <div className="space-y-2">
                <Label>Description</Label>
                <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="État cosmétique, accessoires inclus..." />
              </div>

              {/* Photos */}
              <div className="space-y-2">
                <Label>Photos ({(editingProduct?.photos?.length || 0) + pendingPhotos.length}/5)</Label>
                
                {/* Existing photos (edit mode) */}
                {editingProduct?.photos?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {editingProduct.photos.map((url, idx) => (
                      <div key={idx} className="relative group w-20 h-20 rounded-lg border overflow-hidden">
                        <img src={`${BACKEND}${url}`} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          onClick={() => handlePhotoDelete(editingProduct.id, url)}
                        >
                          <X className="h-4 w-4 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pending photos preview */}
                {pendingPhotos.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {pendingPhotos.map((p, idx) => (
                      <div key={idx} className="relative group w-20 h-20 rounded-lg border overflow-hidden">
                        <img src={p.preview} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          onClick={() => removePendingPhoto(idx)}
                        >
                          <X className="h-4 w-4 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload buttons */}
                {(editingProduct?.photos?.length || 0) + pendingPhotos.length < 5 && (
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => dialogFileRef.current?.click()}>
                      <ImageIcon className="h-4 w-4 mr-1.5" />
                      Choisir fichier
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.accept = "image/*";
                      input.capture = "environment";
                      input.onchange = (e) => { if (e.target.files.length) addPendingPhotos(e.target.files); };
                      input.click();
                    }}>
                      <Camera className="h-4 w-4 mr-1.5" />
                      Prendre photo
                    </Button>
                    <input
                      ref={dialogFileRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files.length) addPendingPhotos(e.target.files);
                        e.target.value = "";
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" data-testid="submit-product-btn">
                {editingProduct ? "Modifier" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
